import React from "react"
import { DataTable, Button, Select, Input, Icon } from '@arnsbogroup/react-voicearchive'
import messages from "./messages"
import { useIntl, FormattedMessage } from 'react-intl'
import useApi from 'hooks/useApi'

const BuyoutsTable = ({ costCurrency, priceCurrency, buyouts = [], onBuyoutChange, editing = false }) => {
  const { formatMessage } = useIntl()
  const [mediaOptions] = useApi(`/rest/v2/ordertargetmedia?search[hidden]=0&search[deleted]=0&pageSize=999`)
  const [usageOptions] = useApi(`/rest/v2/orderusage`)
  const [currencyOptions] = useApi(`/rest/v1/currency?pageSize=999`)
  const [quantityOptions] = useApi(`/rest/v2/order/audition-scope-quantity-options`)
  const [territoryMarketOptions] = useApi(`/rest/v2/territoryMarket?search[deleted]=0&pageSize=999`)
  const emptyBuyout = {
    quantity: 0,
    quantity_type: {
      value: 'SPOT',
      label: 'Spot'
    },
    media: {
      value: '',
      label: ''
    },
    usage: {
      value: '',
      label: ''
    },
    territoryMarket: {
      value: '',
      label: ''
    },
    cost: 0,
    costCurrency: costCurrency ? costCurrency : {
      value: '',
      label: ''
    },
    price: 0,
    priceCurrency: priceCurrency ? priceCurrency : {
      value: '',
      label: ''
    },
  }
  const updateBuyoutEntry = (key, entry, value) => {
    if (key == 'costCurrency' || key == 'priceCurrency') {
      buyouts.map((buyout) => buyout[key] = value)
    } else {
      const index = buyouts.findIndex(item => 
        item.quantity == entry.quantity &&
        item.media == entry.media &&
        item.usage == entry.usage &&
        item.costCurrency == entry.costCurrency &&
        item.priceCurrency == entry.priceCurrency
      )
      buyouts[index] = ({...buyouts[index], [key]: value})
    }

    onBuyoutChange(buyouts)
  }

  const removeBuyout = (entry) => {
    const indexToRemove = buyouts.findIndex(item => 
      item.quantity == entry.quantity &&
      item.quantity_type == entry.quantity_type &&
      item.media == entry.media &&
      item.usage == entry.usage &&
      item.cost == entry.cost &&
      item.costCurrency == entry.costCurrency &&
      item.priceCurrency == entry.priceCurrency
    )
    if (indexToRemove !== -1) {
      buyouts.splice(indexToRemove, 1)
      onBuyoutChange(buyouts)
    }
  }

  const addNewBuyout = (newBuyout) => {
    buyouts.push(newBuyout)
    onBuyoutChange(buyouts)
  }

  return (
    <div>
      <DataTable
        noDataMessage={formatMessage(messages.noData)}
        className="w-full"
        head={{
          quantity: { label: formatMessage(messages.tableHeadSpotQuantity), className: 'w-1/6' },
          media: { label: formatMessage(messages.tableHeadMedia), className: 'w-1/6' },
          usage: { label: formatMessage(messages.tableHeadUsage), className: 'w-1/6' },
          territoryMarkets: { label: formatMessage(messages.tableHeadTerritoryMarket), className: 'w-1/6' },
          cost: { label: formatMessage(messages.tableHeadCost), className: 'w-1/6' },
          price: { label: formatMessage(messages.tableHeadPrice), className: 'w-1/6' },
          action: {label: <Icon icon={['fas', 'ellipsis-v']} />, className: 'w-16'}
        }}
        body={{
          quantity: (entry) =>
            <div className="flex flex-col-2 gap-1">
              <Input
                size="sm"
                defaultValue={ entry.quantity }
                onBlur={({ target: { value } }) => {
                  updateBuyoutEntry('quantity', entry, value)
                }}
                disabled={editing}
              />
              <Select
                containerProps={{className: 'w-full'}}
                styles={{container: (provided, state) => ({
                  ...provided,
                  background: 'white',
                })}}
                size="sm"
                value={[entry.quantity_type]}
                onChange={(value) => updateBuyoutEntry('quantity_type', entry, value)}
                defaultOptions
                options={quantityOptions?.map(({ id, name }) => ({ value: id, label: name }))}
                isDisabled={editing}
              />
            </div>,
          media: (entry) =>
            <Select
              styles={{container: (provided, state) => ({
                ...provided,
                background: 'white',
              })}}
              size="sm"
              value={[entry.media]}
              onChange={(value) => updateBuyoutEntry('media', entry, value)}
              defaultOptions
              options={mediaOptions?.map(({ uid, name }) => ({ value: uid, label: name }))}
              isDisabled={editing}
            />,
          territoryMarkets: (entry) =>
            <Select
              styles={{container: (provided, state) => ({
                ...provided,
                background: 'white',
              })}}
              size="sm"
              value={[entry.territoryMarket]}
              onChange={(value) => updateBuyoutEntry('territoryMarket', entry, value)}
              options={territoryMarketOptions?.map(({ uid, name }) => ({ value: +uid, label: name }))}
              defaultOptions
              isDisabled={editing}
            />,
          usage: (entry) =>
            <Select
              styles={{container: (provided, state) => ({
                ...provided,
                background: 'white',
              })}}
              size="sm"
              value={[entry.usage]}
              onChange={(value) => updateBuyoutEntry('usage', entry, value)}
              options={usageOptions?.map(({ uid, name }) => ({ value: uid, label: name }))}
              defaultOptions
              isDisabled={editing}
            />,
          cost: (entry) =>
            <div className="flex flex-col-2 gap-1">
              <Input
                type='number'
                min='0'
                size="sm"
                defaultValue={ entry.cost }
                onBlur={({ target: { value } }) => {
                  updateBuyoutEntry('cost', entry, value)
                }}
              />
              <Select
                containerProps={{className: 'w-full'}}
                styles={{container: (provided, state) => ({
                  ...provided,
                  background: 'white',
                })}}
                size="sm"
                value={entry.costCurrency}
                onChange={(value) => updateBuyoutEntry('costCurrency', entry, value)}
                options={currencyOptions?.map(({ uid, iso }) => ({ value: uid, label: iso }))}
                defaultOptions
                isDisabled={editing}
              />
            </div>,
          price: (entry) =>
            <div className="flex flex-col-2 gap-1">
              <Input
                type='number'
                min='0'
                size="sm"
                defaultValue={ entry.price }
                onBlur={({ target: { value } }) => {
                  updateBuyoutEntry('price', entry, value)
                }}
              />
              <Select
                containerProps={{className: 'w-full'}}
                styles={{container: (provided, state) => ({
                  ...provided,
                  background: 'white',
                })}}
                size="sm"
                value={entry.priceCurrency}
                onChange={(value) => updateBuyoutEntry('priceCurrency', entry, value)}
                options={currencyOptions?.map(({ uid, iso }) => ({ value: uid, label: iso }))}
                defaultOptions
                isDisabled={editing}
              />
            </div>,
          action: (entry) => 
          <span className={editing ? 'cursor-not-allowed' : 'cursor-pointer'}
            onClick={() => editing ? undefined : removeBuyout(entry)}
          >
            <Icon className={editing ? 'text-gray-300' : 'text-negative'} icon={['fas', 'trash']} />
          </span>
        }}
        entries={buyouts}
      />
      <div className="mt-2 text-right p-2">
        <Button
          size="xs"
          onClick={() =>
            addNewBuyout({
              ...emptyBuyout,
              costCurrency: buyouts[0]?.costCurrency || emptyBuyout.costCurrency,
              priceCurrency: buyouts[0]?.priceCurrency || emptyBuyout.priceCurrency,
            })
          }
          disabled={editing}
        >
          <FormattedMessage {...messages.addBuyout} />
        </Button>
      </div>
    </div>
  )
}

export default BuyoutsTable
